import { onCleanup, onMount } from 'solid-js'

import { type ViewTransitionDirection, activeTab } from '../../state/appUiStore'
import { activateAdjacentTab } from './tabNavigationActions'

const EDITABLE_TARGET_SELECTOR =
   'input, textarea, select, [contenteditable="true"], [contenteditable="plaintext-only"]'

type KeyboardTarget = EventTarget & {
   closest?: (selector: string) => Element | null
}

/**
 * Resolves app tab navigation direction from horizontal arrow keys.
 *
 * @param key - The keyboard event key.
 * @returns The tab direction, or null for unrelated keys.
 */
export function getKeyboardTabNavigationDirection(
   key: string,
): ViewTransitionDirection | null {
   if (key === 'ArrowRight') {
      return 'forward'
   }
   if (key === 'ArrowLeft') {
      return 'backward'
   }
   return null
}

/**
 * Checks whether a keydown should keep its native control behavior.
 *
 * @param event - The keyboard event.
 * @returns True when tab navigation should ignore the event.
 */
export function shouldIgnoreTabNavigationKeydown(event: KeyboardEvent): boolean {
   if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) {
      return true
   }
   const target = event.target as KeyboardTarget | null
   if (target?.closest?.(EDITABLE_TARGET_SELECTOR)) {
      return true
   }
   return Boolean(
      typeof document !== 'undefined' &&
      document.querySelector('dialog[open], [role="dialog"]:not(.hidden):not([hidden])'),
   )
}

/**
 * Enables arrow-key tab navigation while the page is active.
 */
export function useTabKeyboardNavigation() {
   onMount(() => {
      /**
       * Handles page-level arrow-key navigation.
       *
       * @param event - The keydown event.
       */
      const handleKeyDown = (event: KeyboardEvent) => {
         if (shouldIgnoreTabNavigationKeydown(event)) {
            return
         }
         const direction = getKeyboardTabNavigationDirection(event.key)
         if (!direction) {
            return
         }
         const tabButtonFocused = Boolean(
            document.activeElement?.closest('#tab-button-group'),
         )
         if (activateAdjacentTab(direction)) {
            event.preventDefault()
            // A clicked tab keeps focus; follow the active tab so the arrow key's
            // focus-visible ring doesn't paint on the tab we just left.
            if (tabButtonFocused) {
               document.getElementById(`${activeTab()}-tab-btn`)?.focus()
            }
         }
      }

      window.addEventListener('keydown', handleKeyDown)
      onCleanup(() => {
         window.removeEventListener('keydown', handleKeyDown)
      })
   })
}
